import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Video,
  Plus,
  Sparkles,
  Users,
  CalendarClock,
  Clock,
  Settings,
  Trash2,
  MoreVertical,
  Archive,
  XCircle,
  CheckCircle2,
  ExternalLink,
  Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { formatInTz, formatTimeInTz, COMMON_TIMEZONES, userBrowserTimezone } from "@/lib/time";
import DashboardTaskWidget from "@/components/DashboardTaskWidget";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STATS_META = [
  { key: "meetings_this_week", label: "Meetings this week", icon: Video },
  { key: "hours_saved", label: "Hours saved", icon: Clock, suffix: "h" },
  { key: "followups_pending", label: "Follow-ups generated", icon: Sparkles },
  { key: "bookings_received", label: "Bookings received", icon: CalendarClock },
];

export default function Dashboard() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clocks, setClocks] = useState(() => {
    const saved = localStorage.getItem("bloq_world_clocks");
    if (saved) return JSON.parse(saved);
    return [userBrowserTimezone(), "America/New_York", "Asia/Tokyo"];
  });
  const [now, setNow] = useState(new Date());
  const [showClockEditor, setShowClockEditor] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    localStorage.setItem("bloq_world_clocks", JSON.stringify(clocks));
  }, [clocks]);

  useEffect(() => {
    if (!currentWorkspace) return;
    setLoading(true);
    api
      .get(`/dashboard/stats?workspace_id=${currentWorkspace.workspace_id}`)
      .then((r) => setStats(r.data))
      .catch((e) => toast.error("Could not load dashboard stats"))
      .finally(() => setLoading(false));
  }, [currentWorkspace]);

  const createInstant = async () => {
    if (!currentWorkspace) return;
    try {
      const { data } = await api.post("/meetings", {
        title: "Instant meeting",
        workspace_id: currentWorkspace.workspace_id,
        duration_minutes: 60,
        timezone: user?.timezone || userBrowserTimezone(),
        guest_access_enabled: currentWorkspace?.instant_meeting_guest_access_default ?? true,
        waiting_room_enabled: currentWorkspace?.instant_meeting_waiting_room_default ?? false,
        ai_summary_enabled: currentWorkspace?.instant_meeting_ai_summary_default ?? true,
        auto_summary_on_end: currentWorkspace?.instant_meeting_auto_summary_on_end_default ?? true,
      });
      navigate(`/m/${data.public_id}`);
    } catch (e) {
      toast.error("Could not start meeting");
    }
  };

  const reloadStats = () => {
    if (!currentWorkspace) return;
    api
      .get(`/dashboard/stats?workspace_id=${currentWorkspace.workspace_id}`)
      .then((r) => setStats(r.data))
      .catch(() => {});
  };

  // Meeting row actions (PATCH /meetings/{id} accepts status / is_archived)
  const meetingAction = async (it, action) => {
    try {
      if (action === "end") {
        if (!window.confirm("End this meeting for everyone?")) return;
        await api.patch(`/meetings/${it.id}`, { status: "ended" });
        // Best-effort broadcast so anyone still in the live room is kicked
        // out within ~2s (the /control-events poll cadence in MeetingRoom).
        // Mirrors what Meetings.jsx::endMeeting and the in-meeting
        // "End for all" button already do — Dashboard's path was missing
        // this broadcast so participants never saw the kick signal.
        try { await api.post(`/meetings/${it.id}/host/end-for-all`, {}); } catch {}
        toast.success("Meeting ended");
      } else if (action === "cancel") {
        if (!window.confirm("Cancel this meeting?")) return;
        await api.patch(`/meetings/${it.id}`, { status: "canceled" });
        toast.success("Meeting canceled");
      } else if (action === "archive") {
        await api.patch(`/meetings/${it.id}`, { is_archived: true });
        toast.success("Meeting archived");
      }
      reloadStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Booking row actions
  const bookingAction = async (it, action) => {
    try {
      if (action === "complete") {
        await api.post(`/booking/bookings/${it.id}/mark-complete`);
        toast.success("Booking marked complete");
      } else if (action === "cancel") {
        if (!window.confirm("Cancel this booking?")) return;
        await api.post(`/booking/bookings/${it.id}/cancel`, { reason: "Canceled by host" });
        toast.success("Booking canceled");
      }
      reloadStats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyBookingLink = null;
  void copyBookingLink;
  return (
    <div className="flex flex-col gap-8" data-testid="dashboard-page">
      <header>
        <p className="bloq-eyebrow">Welcome back, {user?.name?.split(" ")[0] || "friend"}</p>
        <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">
          Your meetings, minus the mayhem.
        </h1>
      </header>

      {/* Top row: 2 primary actions (extra-wide) + 3 world clocks (tight).
          On narrow screens the clocks wrap below the buttons. */}
      <div className="grid lg:grid-cols-7 gap-3" data-testid="dashboard-top-row">
        <Link
          to="/app/meetings/new"
          data-testid="quick-action-schedule"
          className="bloq-card p-4 lg:col-span-2 flex items-center justify-between text-left hover:-translate-y-0.5 transition-transform"
        >
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(0,94,122,0.1)", color: "#005e7a" }}>
              <Plus className="w-5 h-5" />
            </span>
            <div>
              <p className="font-semibold text-sm">Schedule a meeting</p>
              <p className="text-xs text-[#82828c]">Pick date, time, invitees</p>
            </div>
          </div>
        </Link>
        <button
          onClick={createInstant}
          data-testid="quick-action-instant"
          className="bloq-card p-4 lg:col-span-2 flex items-center justify-between text-left hover:-translate-y-0.5 transition-transform"
        >
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(0,94,122,0.1)", color: "#005e7a" }}>
              <Video className="w-5 h-5" />
            </span>
            <div>
              <p className="font-semibold text-sm">Start instant meeting</p>
              <p className="text-xs text-[#82828c]">Lobby opens immediately</p>
            </div>
          </div>
        </button>
        {/* Tighter world-clocks card — col-span shrunk from 3 → 3 of 7
            (was 3 of 5), giving more breathing room to the two CTAs above. */}
        <div className="bloq-card p-3 lg:col-span-3" data-testid="world-clocks-card">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-medium">World clocks</p>
            <button
              className="text-xs text-[#005e7a] font-medium hover:underline"
              onClick={() => setShowClockEditor(true)}
              data-testid="edit-clocks-button"
            >
              Edit
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {clocks.map((tz) => (
              <div key={tz} className="flex flex-col min-w-0">
                <p className="bloq-display text-lg leading-tight truncate" data-testid={`world-clock-time-${tz}`}>{formatTimeInTz(now.toISOString(), tz)}</p>
                <p className="text-[10px] text-[#82828c] truncate">{tz.replace(/_/g, " ")}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {STATS_META.map((s) => {
          const Icon = s.icon;
          const v = stats?.stats?.[s.key];
          return (
            <div key={s.key} className="bloq-stat" data-testid={`stat-${s.key}`}>
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-medium">{s.label}</p>
                <Icon className="w-4 h-4 text-[#82828c]" />
              </div>
              <p className="bloq-display text-4xl mt-1">
                {loading ? "—" : v ?? 0}
                {s.suffix && <span className="text-base text-[#82828c] font-normal">{s.suffix}</span>}
              </p>
            </div>
          );
        })}
      </div>

      {/* Two-column: combined "Upcoming" (meetings + bookings) + tasks */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="bloq-card p-6 lg:col-span-2" data-testid="upcoming-card">
          <div className="flex items-center justify-between mb-4">
            <p className="bloq-headline text-xl text-[#005e7a]">Upcoming</p>
            <div className="flex gap-2">
              <Link to="/app/meetings" className="text-xs text-[#005e7a] font-medium hover:underline" data-testid="view-all-meetings-link">All meetings</Link>
              <span className="text-xs text-[#82828c]">·</span>
              <Link to="/app/bookings" className="text-xs text-[#005e7a] font-medium hover:underline" data-testid="view-all-bookings-link">All bookings</Link>
            </div>
          </div>
          {loading ? (
            <p className="text-sm text-[#82828c]">Loading…</p>
          ) : (() => {
            // Merge meetings + bookings into a single sorted timeline.
            // Each entry carries a `kind` so we can render the right icon
            // and route action.
            const items = [
              ...(stats?.upcoming || []).map((m) => ({
                kind: "meeting",
                id: m.meeting_id,
                public_id: m.public_id,
                title: m.title,
                start: m.start_time,
                duration: m.duration_minutes,
                status: m.status,
              })),
              ...(stats?.upcoming_bookings || []).map((b) => ({
                kind: "booking",
                id: b.booking_id,
                title: b.invitee_name ? `${b.invitee_name} — ${b.location_label || "Appointment"}` : (b.location_label || "Booking"),
                start: b.start_time_utc,
                end: b.end_time_utc,
                status: b.status,
                location_type: b.location_type,
                meeting_public_id: b.meeting_public_id,
                external_join_url: b.external_join_url || "",
              })),
            ].sort((a, b) => new Date(a.start) - new Date(b.start)).slice(0, 8);
            if (!items.length) {
              return (
                <EmptyMini
                  t="Nothing on the calendar yet"
                  d="Schedule a meeting, run an instant one, or share your booking link to fill this up."
                />
              );
            }
            return (
              <ul className="flex flex-col gap-1">
                {items.map((it) => {
                  const isMeeting = it.kind === "meeting";
                  // Booking join URL precedence: Bloq video meeting room
                  // (only when location resolved to bloq_video / hybrid),
                  // or the host's pasted external link (Zoom / Meet / Teams)
                  // when location is external_link. Other types (in-person,
                  // phone, custom) → no Join button.
                  const isBloqOnline = !isMeeting && ["bloq_video", "hybrid"].includes(it.location_type) && !!it.meeting_public_id;
                  const isBookingExternal = !isMeeting && it.location_type === "external_link" && !!it.external_join_url;
                  const bookingJoinHref = isBloqOnline
                    ? `/m/${it.meeting_public_id}`
                    : isBookingExternal
                      ? it.external_join_url
                      : "";
                  const canJoinBooking = !!bookingJoinHref;
                  return (
                    <li
                      key={`${it.kind}-${it.id}`}
                      className="flex items-center justify-between gap-3 py-2.5 px-3 rounded-lg hover:bg-[#f5efe6]"
                      data-testid={`upcoming-row-${it.kind}-${it.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {isMeeting ? (
                          it.status === "live" ? <span className="bloq-live-dot flex-shrink-0" /> : <Video className="w-4 h-4 text-[#005e7a] flex-shrink-0" />
                        ) : (
                          <CalendarClock className="w-4 h-4 text-[#b45309] flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{it.title}</p>
                          <p className="text-xs text-[#82828c]">
                            {formatInTz(it.start, user?.timezone || userBrowserTimezone())}
                            {it.duration ? ` · ${it.duration}m` : ""}
                            {!isMeeting && (
                              <span className="ml-1 text-[10px] uppercase tracking-widest text-[#b45309]">· booking</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {isMeeting ? (
                          <button
                            onClick={() => navigate(`/m/${it.public_id}`)}
                            className="bloq-btn-secondary px-3 py-1.5 text-xs"
                            data-testid={`join-meeting-${it.id}`}
                          >
                            {it.status === "live" ? "Join now" : "Lobby"}
                          </button>
                        ) : canJoinBooking ? (
                          isBookingExternal ? (
                            <a
                              href={bookingJoinHref}
                              target="_blank"
                              rel="noreferrer"
                              className="bloq-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1"
                              data-testid={`join-booking-${it.id}`}
                            >
                              Join <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <button
                              onClick={() => navigate(bookingJoinHref)}
                              className="bloq-btn-secondary px-3 py-1.5 text-xs"
                              data-testid={`join-booking-${it.id}`}
                            >
                              Join
                            </button>
                          )
                        ) : (
                          <Link
                            to={`/app/bookings?bookingId=${it.id}`}
                            className="bloq-btn-secondary px-3 py-1.5 text-xs"
                            data-testid={`open-booking-${it.id}`}
                          >
                            Open
                          </Link>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="w-7 h-7 inline-flex items-center justify-center hover:bg-[#e5d6c2] rounded-md"
                              data-testid={`upcoming-menu-${it.kind}-${it.id}`}
                              aria-label="More actions"
                            >
                              <MoreVertical className="w-4 h-4 text-[#4a4a52]" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            {isMeeting ? (
                              <>
                                <DropdownMenuItem
                                  onClick={() => navigate(`/app/meetings/${it.id}`)}
                                  data-testid={`menu-meeting-details-${it.id}`}
                                >
                                  <LinkIcon className="w-3.5 h-3.5 mr-2" /> Open details
                                </DropdownMenuItem>
                                {it.status !== "ended" && it.status !== "canceled" && (
                                  <DropdownMenuItem
                                    onClick={() => meetingAction(it, "end")}
                                    data-testid={`menu-meeting-end-${it.id}`}
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> End meeting
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => meetingAction(it, "archive")}
                                  data-testid={`menu-meeting-archive-${it.id}`}
                                >
                                  <Archive className="w-3.5 h-3.5 mr-2" /> Archive
                                </DropdownMenuItem>
                                {it.status !== "canceled" && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => meetingAction(it, "cancel")}
                                      className="text-[#c43838] focus:text-[#c43838]"
                                      data-testid={`menu-meeting-cancel-${it.id}`}
                                    >
                                      <XCircle className="w-3.5 h-3.5 mr-2" /> Cancel meeting
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                <DropdownMenuItem asChild data-testid={`menu-booking-details-${it.id}`}>
                                  <Link to={`/app/bookings?bookingId=${it.id}`}>
                                    <LinkIcon className="w-3.5 h-3.5 mr-2" /> Open details
                                  </Link>
                                </DropdownMenuItem>
                                {it.status === "confirmed" && (
                                  <DropdownMenuItem
                                    onClick={() => bookingAction(it, "complete")}
                                    data-testid={`menu-booking-complete-${it.id}`}
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> Mark complete (end)
                                  </DropdownMenuItem>
                                )}
                                {it.status !== "canceled" && it.status !== "completed" && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => bookingAction(it, "cancel")}
                                      className="text-[#c43838] focus:text-[#c43838]"
                                      data-testid={`menu-booking-cancel-${it.id}`}
                                    >
                                      <XCircle className="w-3.5 h-3.5 mr-2" /> Cancel booking
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </li>
                  );
                })}
              </ul>
            );
          })()}
        </div>

        {/* Extended Tasks column — Recent Participants removed per user
            request (it was low-signal noise; tasks are higher-value). */}
        <div className="flex flex-col gap-5">
          <DashboardTaskWidget />
        </div>
      </div>

      <ClockEditor open={showClockEditor} onOpenChange={setShowClockEditor} clocks={clocks} setClocks={setClocks} />
    </div>
  );
}

function EmptyMini({ t, d }) {
  return (
    <div className="py-8 text-center">
      <p className="font-medium">{t}</p>
      <p className="text-sm text-[#82828c] mt-1">{d}</p>
    </div>
  );
}

function ClockEditor({ open, onOpenChange, clocks, setClocks }) {
  const [local, setLocal] = useState(clocks);
  useEffect(() => setLocal(clocks), [clocks, open]);
  const save = () => {
    setClocks(local);
    onOpenChange(false);
    toast.success("World clocks updated");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit world clocks</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 mt-2">
          {[0, 1, 2].map((i) => (
            <div key={i}>
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
                Clock {i + 1}
              </label>
              <Select value={local[i]} onValueChange={(v) => { const next = [...local]; next[i] = v; setLocal(next); }}>
                <SelectTrigger className="mt-1" data-testid={`clock-${i}-select`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
          <button onClick={save} className="bloq-btn-primary" data-testid="save-clocks-button">
            Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
